import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AuditLogCategory,
  DateFormat,
  DateTimeSettings,
  SYSTEM_ACTION_TYPES,
  TIMEZONE_OPTIONS,
  Timezone,
} from '@cdw-ae/common-data';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CdwRadioButtonModule } from '@cdw-ae/common-ui-components-radio-button';
import { CdwSelectModule } from '@cdw-ae/common-ui-components-select';
import { CdwSelectOption } from '@cdw-ae/shared-interfaces';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Store } from '@ngrx/store';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { updateCustomerSettings } from '../../../../../../+state/user-identity/user-identity-settings.actions';
import type { UserData } from '../../../../../../+state/user-identity/user-identity-settings.models';
import {
  selectCurrentUserData,
  selectCustomerDateTimeSettings,
} from '../../../../../../+state/user-identity/user-identity-settings.selectors';
import { ApiUrlBuilderService } from '../../../../../../common/services/api-url-builder.service';
import { CustomerSettingsService } from '../../../../../../common/services/customer/customer-settings.service';

@Component({
  selector: 'time-zone-date-format',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CdwSelectModule,
    CdwButtonModule,
    CdwIconModule,
    CdwRadioButtonModule,
  ],
  templateUrl: './time-zone-date-format.component.html',
  styleUrl: './time-zone-date-format.component.scss',
})
export class TimeZoneDateFormatComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Expose enum for template use
  public readonly DateFormat = DateFormat;

  public timezoneValues: CdwSelectOption[];
  selectedTimeZone!: Timezone;
  selectedTimeZoneName = '';
  selectedDateFormat!: DateFormat;
  hasChanges = false;
  isSaving = false;
  showTimezoneMismatchBanner = false;
  detectedBrowserTimezoneName = '';

  private readonly snackbarService: SnackbarService = inject(SnackbarService);
  private readonly store: Store = inject(Store);
  private readonly customerSettingsService = inject(CustomerSettingsService);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly timezones = TIMEZONE_OPTIONS;
  private readonly urlBuilder = inject(ApiUrlBuilderService);
  private originalTimeZone!: Timezone;
  private originalDateFormat!: DateFormat;
  private customerId: number | null = null;
  private dbUserId = 0;
  private detectedBrowserTimezone: Timezone | null = null;

  dateTimeSettings$!: Observable<DateTimeSettings>;
  userData$!: Observable<UserData | null>;

  constructor() {
    this.dateTimeSettings$ = this.store.select(selectCustomerDateTimeSettings);
    this.userData$ = this.store.select(selectCurrentUserData);
    this.timezoneValues = this.getTimeZoneOptions();
  }

  ngOnInit(): void {
    this.userData$.pipe(takeUntil(this.destroy$)).subscribe((userData) => {
      this.customerId = userData?.claims?.core?.customerId ?? null;
      this.dbUserId = userData?.claims?.core?.dbUserId || 0;
      this.cdr.markForCheck();
    });

    this.dateTimeSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.selectedTimeZone = settings.timezone;
        this.selectedTimeZoneName =
          this.timezones.find((tz) => tz.value === this.selectedTimeZone)
            ?.name || '';
        this.selectedDateFormat = settings.dateFormat;
        this.originalTimeZone = this.selectedTimeZone;
        this.originalDateFormat = this.selectedDateFormat;
        this.hasChanges = false;

        this.detectedBrowserTimezone = this.detectBrowserTimezone();
        this.detectedBrowserTimezoneName = this.detectedBrowserTimezone
          ? (this.timezones.find(
              (tz) => tz.value === this.detectedBrowserTimezone
            )?.name ?? '')
          : '';
        this.showTimezoneMismatchBanner = this.computeTimezoneMismatch(
          settings.timezone
        );

        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getTimeZoneOptions(): CdwSelectOption[] {
    return this.timezones.map((tz) => ({
      displayName: `${tz.name}           (${this.getCurrentTimeInTimezone(tz.value)})`,
      value: tz.value,
    }));
  }

  public onTimeZoneChange(value: Timezone): void {
    this.selectedTimeZone = value;
    this.selectedTimeZoneName =
      this.timezones.find((tz) => tz.value === value)?.name || '';
    this.checkForChanges();
  }

  public onDateFormatChange(value: DateFormat): void {
    this.selectedDateFormat = value;
    this.checkForChanges();
  }

  public saveSettings(): void {
    if (this.customerId === null) {
      this.snackbarService.showMessage(
        'Unable to save: Customer ID not found',
        'OK',
        {
          horizontalPosition: 'center',
        }
      );
      return;
    }

    this.isSaving = true;

    this.customerSettingsService
      .updateDateTimeSettings({
        timezone: this.selectedTimeZone,
        dateFormat: this.selectedDateFormat,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Create audit log for timezone settings change
          this.createTimezoneSettingsAuditLog();

          // Update the store with the new settings
          this.store.dispatch(
            updateCustomerSettings({
              timezone: this.selectedTimeZone,
              dateFormat: this.selectedDateFormat,
            })
          );

          // After successful save, update the original values and reset hasChanges
          this.originalTimeZone = this.selectedTimeZone;
          this.originalDateFormat = this.selectedDateFormat;
          this.hasChanges = false;
          this.isSaving = false;
          this.cdr.markForCheck();

          // Show success message
          this.snackbarService.showMessage(
            'Your settings have been saved',
            'OK',
            {
              horizontalPosition: 'center',
            }
          );
        },
        error: (error) => {
          this.isSaving = false;
          this.cdr.markForCheck();
          // biome-ignore lint/suspicious/noConsole: error telemetry
          console.error('Failed to save settings:', error);
          this.snackbarService.showMessage(
            'Failed to save settings. Please try again.',
            'OK',
            {
              horizontalPosition: 'center',
            }
          );
        },
      });
  }

  public applyBrowserTimezone(): void {
    if (this.detectedBrowserTimezone && !this.isSaving) {
      this.onTimeZoneChange(this.detectedBrowserTimezone);
      this.showTimezoneMismatchBanner = false;
    }
  }

  private computeTimezoneMismatch(storedTimezone: Timezone): boolean {
    if (!this.detectedBrowserTimezone) return false;
    return this.detectedBrowserTimezone !== storedTimezone;
  }

  private detectBrowserTimezone(): Timezone | null {
    try {
      const iana = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const match = this.timezones.find((tz) => tz.value === iana);
      return match ? (match.value as Timezone) : null;
    } catch {
      return null;
    }
  }

  private getCurrentTimeInTimezone(timezone: string): string {
    const now = new Date();
    return now.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  private checkForChanges(): void {
    this.hasChanges =
      this.selectedTimeZone !== this.originalTimeZone ||
      this.selectedDateFormat !== this.originalDateFormat;
  }

  /**
   * Create audit log for timezone settings change
   * Note: Settings changes are immediate operations, not background jobs, so jobId is null
   */
  private createTimezoneSettingsAuditLog(): void {
    if (!this.dbUserId) {
      return;
    }

    const operationType = SYSTEM_ACTION_TYPES.TIMEZONE_SETTINGS_CHANGED;
    const action = 'Timezone Settings Changed';
    const description = 'Changed timezone and/or date format settings';

    // Get the timezone name for better readability in audit logs
    const timezoneName =
      this.timezones.find((tz) => tz.value === this.selectedTimeZone)?.name ||
      this.selectedTimeZone;
    const oldTimezoneName =
      this.timezones.find((tz) => tz.value === this.originalTimeZone)?.name ||
      this.originalTimeZone;

    const actionData = {
      userId: this.dbUserId,
      action,
      category: AuditLogCategory.CONFIGURATION,
      operationType,
      description,
      jobId: null,
      changes: {
        timezone: {
          from: this.originalTimeZone,
          to: this.selectedTimeZone,
          fromDisplay: oldTimezoneName,
          toDisplay: timezoneName,
        },
        dateFormat: {
          from: this.originalDateFormat,
          to: this.selectedDateFormat,
        },
      },
    };

    const auditLog = {
      userId: this.dbUserId,
      category: AuditLogCategory.CONFIGURATION,
      operationType,
      action,
      actionData: JSON.stringify(actionData),
      jobId: null,
    };

    // Send audit log to API
    this.httpClient
      .post<{ success: boolean }>(
        this.urlBuilder.backendApi('audit-log', 'create'),
        auditLog
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          // Audit log created successfully
        },
        error: (error) => {
          // biome-ignore lint/suspicious/noConsole: audit log failures surface in dev tools without disrupting the user
          console.error('Failed to create audit log:', error);
        },
      });
  }
}
